import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { AppRoutes } from './routes.tsx';
import { ErrorBoundary } from './features/error-boundary.tsx';
import './styles.css';

// Error tracking, loaded AFTER first paint. Sentry is ~96 KB of source
// that nothing on screen depends on, and the people most affected by an
// extra 30 KB in the critical path are the ones on gym wifi. Fire and
// forget: a failed load must never stop the app rendering.
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
if (SENTRY_DSN) {
  void import('@sentry/react')
    .then((Sentry) => {
      Sentry.init({
        dsn: SENTRY_DSN,
        environment: import.meta.env.MODE,
        tracesSampleRate: 0.1,
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 0.1,
      });
    })
    .catch(() => {
      /* monitoring is best-effort */
    });
}

const root = document.getElementById('root');
if (!root) throw new Error('root element missing');

createRoot(root).render(
  <StrictMode>
    <ErrorBoundary>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </ErrorBoundary>
  </StrictMode>,
);

// Drop the inline boot splash from index.html now that React has painted.
// Done after render() so there's no flash of empty page between the two.
const splash = document.getElementById('boot-splash');
if (splash) {
  splash.style.transition = 'opacity 180ms ease';
  splash.style.opacity = '0';
  // Long enough for the fade, short enough that a stalled transition
  // event can't leave it covering the app.
  setTimeout(() => splash.remove(), 220);
}
